import React, {
    FC, useCallback, useEffect, useMemo, useState
} from 'react'
import { useDispatch } from 'react-redux'
import { Button as VendorButton } from 'react-bootstrap'
import styled from 'styled-components'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { _ } from 'utils'
import { $gray_900 } from 'utils/colors'
import AssetCardContainer from './AssetCardContainer'

const S = {
    AssetPreview: styled.div`
        height: 100%;
        min-height: 90px;
        height: 180px;
        position: relative;
        flex-grow: 1;
        flex-shrink: 1;
    `,
    Text: styled.p`
        color: ${$gray_900} !important;
        font-weight: 300;
        flex-grow: 0;
        flex-shrink: 0;
    `,
    Image: styled.img`
        margin: auto;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    `,
    NoImage: styled.div`
        display: block;
        width: 100%;
        height: inherit;
        border-radius: 4px;
        border: 2px dashed rgba(24, 28, 33, 0.15);
        box-sizing: border-box;
    `,
    ButtonContainer: styled.div`
        width: 100%;
        position: relative;
        overflow: hidden;
        align-self: flex-end;
        margin-top: auto;
        flex-grow: 0;
        flex-shrink: 0;
    `,
    ChangeButton: styled(VendorButton)`
        position: relative;
        z-index: 1;
    `,
    FileInput: styled.input`
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        opacity: 0;
    `,
}

export interface AssetImageCardProps {
    buttonChangeText: string
    buttonUploadText: string
    defaultValue: string
    onUpdate(file: File): void
    title: string
}

export const validImageExtensions = ['jpg', 'jpeg', 'bmp', 'gif', 'png', 'svg', 'webp']

const AssetImageCard: FC<AssetImageCardProps> = ({
    buttonChangeText,
    buttonUploadText,
    defaultValue,
    onUpdate,
    title,
}: AssetImageCardProps) => {
    const dispatch = useDispatch()
    const [value, setValue] = useState(defaultValue)
    const [uploadedFile, setUploadedFile] = useState<string>()
    const fileUploadChangeHandler = useCallback(
        (event) => {
            if (event.target.files.length > 0) {
                const file: File = event.target.files[0]
                const fileExtension = file.name.split('.').pop()
                if (!validImageExtensions.includes(fileExtension)) {
                    dispatch(showErrorNotification('The uploaded file\'s type is not accepted', 3000))
                    return
                }
                setUploadedFile(URL.createObjectURL(file))
                onUpdate(file)
            }
        },
        [dispatch, onUpdate]
    )

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue, setValue])

    const filename = useMemo(
        (): string => (value && value.substr(value.lastIndexOf('/') + 1)) || '',
        [value]
    )

    const src = uploadedFile || value

    return (
        <AssetCardContainer title={title}>
            <S.AssetPreview className="w-100 text-center">
                {src
                    ? <S.Image src={src} alt="" />
                    : <S.NoImage />}
            </S.AssetPreview>
            <S.Text className="w-100 text-center pt-3 mb-3">
                {filename || _('form.no_image')}
            </S.Text>
            <S.ButtonContainer>
                <S.FileInput type="file" onChange={fileUploadChangeHandler} accept="image/*" />
                <S.ChangeButton
                    variant="primary"
                    className="w-100 mt-auto mb-0"
                >
                    {value ? buttonChangeText : buttonUploadText}
                </S.ChangeButton>
            </S.ButtonContainer>
        </AssetCardContainer>
    )
}

export default AssetImageCard
